import React from 'react'
import { metaSetter } from '@app/utils/meta-setter'
import { BlogWrapper } from '@app/components/blog/blog-wrapper'
import type { BlogPageProps } from '@app/types'
import { BlogLayout } from '@app/components/blog-layout'
import {
  Header,
  Header2,
  Header3,
  Header4,
} from '@app/components/general/header'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { Card } from '@app/components/stateless/card/card'
import { featuredItems } from '@app/data/blog/posts'
import { Author } from '@app/components/stateless/blog/author'
import { BlogDate } from '@app/components/blog/blog-date'
import { Category } from '@app/components/stateless/card/category'
import Trans from 'next-translate/Trans'
import { P } from '@app/components/stateless/typo/p'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'
import { blueLink } from '@app/components/stateless/typo/link-style'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/blog/next-js-to-astro', // todo: fix next.js route
    }),
  }
}

const BLOG_INDEX = 0

// the filtered features - remove first two items that are featured
const featuredFilters = featuredItems.filter(
  (item) => item.category === 'engineering' && item.index !== BLOG_INDEX
)

const blogDate =
  featuredItems.find((item) => item.index === BLOG_INDEX)?.date ?? new Date()

// Blog page - TODO: add second header with more details
function Next(props: BlogPageProps) {
  const { t } = useTranslation('next-js-to-astro')

  return (
    <BlogWrapper {...props} websiteUrl={'/blog'}>
      <div className='px-4 mx-auto container max-w-5xl space-y-8'>
        <div className='space-y-1 pt-12'>
          <BlogDate date={blogDate} />
          <div className='flex place-items-center space-x-4 py-2'>
            <Header className='p-0 md:p-0 lg:p-0 xl:p-0'>{t('header')}</Header>
            <Category category='engineering' pure />
          </div>
          <P>{t('header-details')}</P>
          <figure className='text-center pt-2 flex place-items-center flex-col space-y-2'>
            <img
              src='/img/webflow/astro-compare.webp'
              alt={t('transcript-alt')}
              width={686}
              height={506}
            />
            <figcaption>{t('transcript-caption')}</figcaption>
          </figure>
        </div>

        <div className='space-y-1'>
          <Header2>{t('subtitle')}</Header2>

          <Trans
            i18nKey={'next-js-to-astro:subtitle-details'}
            components={[
              <P key={'s-0'} />,
              <a
                className={blueLink}
                href={'https://astro.build/'}
                key={'s-1'}
                rel={'noreferrer'}
                target={'_blank'}
              />,
              <a
                className={blueLink}
                href={'https://jamstack.org/generators/'}
                rel={'noreferrer'}
                target={'_blank'}
                key={'s-2'}
              />,
            ]}
          />
        </div>

        <div className='space-y-1'>
          <Header3>{t('midtitle')}</Header3>
          <P>{t('midtitle-details')}</P>

          <figure className='text-center pt-2 flex place-items-center flex-col space-y-2'>
            <img
              alt={t('midtitle-alt')}
              src='/img/webflow/dom-astro-next.webp'
              width='2235'
              height='699'
            />
            <figcaption>{t('midtitle-captions')}</figcaption>
          </figure>

          <figure className='text-center pt-2 flex place-items-center flex-col space-y-2'>
            <img
              alt={t('midtitle2-alt')}
              src='/img/webflow/example-blog.webp'
              width='942'
              height='456'
            />
            <figcaption>{t('midtitle2-captions')}</figcaption>
          </figure>
        </div>

        <div className='space-y-1'>
          <Header4>{t('endtitle')}</Header4>
          <Trans
            i18nKey={'next-js-to-astro:endtitle-details'}
            components={[
              <P key={'end-0'} />,
              <a
                className={blueLink}
                href={
                  'https://github.com/j-mendez/portfolio/commit/d426edd78e97023b0ee67d4e3a4f255916bdbfa8'
                }
                key={'end-1'}
                rel={'noreferrer'}
                target={'_blank'}
              />,
              <a
                className={blueLink}
                href={'https://github.com/j-mendez/portfolio'}
                key={'end-2'}
                rel={'noreferrer'}
                target={'_blank'}
              />,
            ]}
          />
        </div>

        <div className='flex flex-col place-items-center place-content-center py-5'>
          <Author
            author={t('jeff-mendez:author')}
            intro={t('jeff-mendez:intro')}
            small
          />
        </div>

        <div className='py-4'>
          <Header4 className='text-center'>{t('blog:related')}</Header4>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-3 pb-12'>
          {featuredFilters.map(
            ({ href, category, date, backgroundImage, index }) => {
              return (
                <Card
                  key={`blog-card-${index}`}
                  category={category}
                  href={href}
                  date={date}
                  authorName='Jeff Mendez'
                  cardTitle={`features.${index}.title`}
                  cardDescription={`features.${index}.details`}
                  backgroundImage={backgroundImage}
                />
              )
            }
          )}
        </div>
      </div>
    </BlogWrapper>
  )
}

Next.getLayout = BlogLayout

export default metaSetter(
  { Next },
  {
    translation: 'next-js-to-astro',
    gql: false,
    rest: false,
  }
)
